/**
 * MetadataTableDrawer Component
 *
 * Detail drawer for viewing record details.
 * Groups fields by form_section and orders by form_order
 * for consistency with create/edit forms.
 */

import { useMemo } from 'react'
import { Drawer, Descriptions, Typography, Space, Button, Tag, Grid } from 'antd'
import { EditOutlined, DeleteOutlined, CloseOutlined } from '@ant-design/icons'
import type { FieldMetadata, FormSection } from '@/services/metadataService'
import { formatCellValue, getStatusBadgeColor, isStatusField } from './utils/columnFormatters'
import { isJsonField, isJsonValue } from './utils/jsonFormatters'
import { JsonValueDisplay } from './components/JsonValueDisplay'
import type { MetadataTableDrawerProps } from './types'

const { Title, Text } = Typography
const { useBreakpoint } = Grid

/**
 * Group fields by form_section
 */
function groupFieldsBySection(
  fields: Record<string, FieldMetadata>,
  formSections: FormSection[],
  record: Record<string, unknown>
): Array<{
  section: FormSection | null
  fields: Array<{ key: string; metadata: FieldMetadata; value: unknown }>
}> {
  const groups: Map<
    string | null,
    Array<{ key: string; metadata: FieldMetadata; value: unknown }>
  > = new Map()

  // Initialize groups for each section
  formSections.forEach((section) => {
    groups.set(section.name, [])
  })
  groups.set(null, []) // Orphan fields

  // Assign fields to groups
  Object.entries(fields).forEach(([key, metadata]) => {
    if (metadata.is_visible === false) return // Skip hidden fields

    const sectionName = metadata.form_section ?? null
    const group = groups.get(sectionName) ?? groups.get(null)!
    group.push({
      key,
      metadata,
      value: record[key],
    })
  })

  // Sort fields within each group by form_order
  groups.forEach((fieldList) => {
    fieldList.sort((a, b) => (a.metadata.form_order ?? 0) - (b.metadata.form_order ?? 0))
  })

  // Build result in section order
  const result: Array<{
    section: FormSection | null
    fields: Array<{ key: string; metadata: FieldMetadata; value: unknown }>
  }> = []

  // Add sections in order
  const sortedSections = [...formSections].sort((a, b) => a.order - b.order)
  sortedSections.forEach((section) => {
    const fieldList = groups.get(section.name)
    if (fieldList && fieldList.length > 0) {
      result.push({ section, fields: fieldList })
    }
  })

  // Add orphan fields at end
  const orphanFields = groups.get(null)
  if (orphanFields && orphanFields.length > 0) {
    result.push({ section: null, fields: orphanFields })
  }

  return result
}

/**
 * Render a field value with appropriate formatting
 */
function renderFieldValue(
  value: unknown,
  metadata: FieldMetadata,
  fieldKey: string
): React.ReactNode {
  if (value == null) {
    return <Text type="secondary">-</Text>
  }

  // Status fields render as tags
  if (isStatusField(fieldKey, metadata) && typeof value === 'string') {
    const color = getStatusBadgeColor(value)
    const tagColors: Record<string, string> = {
      success: 'green',
      processing: 'blue',
      error: 'red',
      warning: 'orange',
      default: 'default',
    }
    return <Tag color={tagColors[color]}>{value}</Tag>
  }

  // Boolean fields
  if (metadata.py_type === 'bool' || metadata.py_type === 'boolean') {
    return value ? (
      <Tag color="green">Yes</Tag>
    ) : (
      <Tag color="default">No</Tag>
    )
  }

  // JSON fields - use interactive popover display
  if (isJsonField(metadata) || isJsonValue(value)) {
    return <JsonValueDisplay value={value} maxHeight={300} />
  }

  // Format other values
  return formatCellValue(value, metadata)
}

/**
 * MetadataTableDrawer - Detail view for a record
 */
export function MetadataTableDrawer({
  model,
  record,
  fields,
  formSections = [],
  open,
  onClose,
  onEdit,
  onDelete,
  canWrite = false,
}: MetadataTableDrawerProps) {
  const screens = useBreakpoint()

  // Responsive drawer width
  const drawerWidth = useMemo(() => {
    if (!screens.md) return '100%' // Mobile: full screen
    if (!screens.lg) return '90vw' // Tablet: 90%
    return 700 // Desktop: fixed width
  }, [screens])

  // Group fields by section
  const groupedFields = useMemo(() => {
    if (!record || !fields) return []
    return groupFieldsBySection(fields, formSections, record)
  }, [record, fields, formSections])

  // Get display title (use name or id field)
  const displayTitle = useMemo(() => {
    if (!record) return model
    return (
      (record.name as string) ||
      (record.id as string) ||
      (record.title as string) ||
      model
    )
  }, [record, model])

  if (!record) return null

  return (
    <Drawer
      title={
        <Space>
          <span>{displayTitle}</span>
        </Space>
      }
      placement="right"
      width={drawerWidth}
      open={open}
      onClose={onClose}
      closable={true}
      closeIcon={<CloseOutlined />}
      extra={
        canWrite && (
          <Space>
            {onEdit && (
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => onEdit(record)}
              >
                Edit
              </Button>
            )}
            {onDelete && (
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={() => onDelete(record)}
              >
                Delete
              </Button>
            )}
          </Space>
        )
      }
    >
      {groupedFields.map((group, groupIndex) => (
        <div key={group.section?.name ?? 'other'} className="metadata-drawer-section">
          {group.section && (
            <>
              <Title level={5} style={{ marginTop: groupIndex > 0 ? 24 : 0 }}>
                {group.section.name}
              </Title>
              {group.section.description && (
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  {group.section.description}
                </Text>
              )}
            </>
          )}

          {!group.section && groupedFields.length > 1 && (
            <Title level={5} style={{ marginTop: 24 }}>
              Other Fields
            </Title>
          )}

          <Descriptions
            column={1}
            size="small"
            bordered
            labelStyle={{ width: '40%', fontWeight: 500 }}
          >
            {group.fields.map(({ key, metadata, value }) => (
              <Descriptions.Item
                key={key}
                label={metadata.title ?? key}
              >
                {renderFieldValue(value, metadata, key)}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </div>
      ))}
    </Drawer>
  )
}

export default MetadataTableDrawer
